/**
 * Potrace helper using npm package 'potrace-js'
 * Converts bitmap images into vector paths (SVG)
 */

import * as PotraceJS from 'potrace-js/src/index.js';
import { Bitmap, traceBitmap, traceCanvas, traceImage, traceUrl, getPaths } from 'potrace-js/src/index.js';

const DEFAULT_OPTIONS = {
    turnpolicy: 'minority',
    turdsize: 2,
    optcurve: true,
    alphamax: 1,
    opttolerance: 0.2,
    threshold: 128,
    color: 'black',
    scale: 1,
};

function createBitmapFromCanvas(canvas, threshold = 128) {
    const { width, height } = canvas;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, width, height);
    const bitmap = new Bitmap(width, height);

    const length = width * height;
    let imageDataIndex = 0;
    for (let bitmapIndex = 0; bitmapIndex < length; bitmapIndex++) {
        const r = imageData.data[imageDataIndex++];
        const g = imageData.data[imageDataIndex++];
        const b = imageData.data[imageDataIndex++];
        const a = imageData.data[imageDataIndex++];

        if (a < 64) {
            bitmap.data[bitmapIndex] = 0;
        } else {
            const color = 0.2126 * r + 0.7153 * g + 0.0721 * b;
            bitmap.data[bitmapIndex] = color < threshold ? 1 : 0;
        }
    }
    return bitmap;
}

function pathListToSVG(pathList, width, height, size = 1, color = 'black') {
    function path(curve) {
        function bezier(i) {
            let b =
                'C ' +
                (curve.c[i * 3 + 0].x * size).toFixed(3) +
                ' ' +
                (curve.c[i * 3 + 0].y * size).toFixed(3) +
                ',';
            b +=
                (curve.c[i * 3 + 1].x * size).toFixed(3) +
                ' ' +
                (curve.c[i * 3 + 1].y * size).toFixed(3) +
                ',';
            b +=
                (curve.c[i * 3 + 2].x * size).toFixed(3) +
                ' ' +
                (curve.c[i * 3 + 2].y * size).toFixed(3) +
                ' ';
            return b;
        }

        function segment(i) {
            let s =
                'L ' +
                (curve.c[i * 3 + 1].x * size).toFixed(3) +
                ' ' +
                (curve.c[i * 3 + 1].y * size).toFixed(3) +
                ' ';
            s +=
                (curve.c[i * 3 + 2].x * size).toFixed(3) +
                ' ' +
                (curve.c[i * 3 + 2].y * size).toFixed(3) +
                ' ';
            return s;
        }

        const n = curve.n;
        let p =
            'M' +
            (curve.c[(n - 1) * 3 + 2].x * size).toFixed(3) +
            ' ' +
            (curve.c[(n - 1) * 3 + 2].y * size).toFixed(3) +
            ' ';
        for (let i = 0; i < n; i++) {
            if (curve.tag[i] === 'CURVE') {
                p += bezier(i);
            } else if (curve.tag[i] === 'CORNER') {
                p += segment(i);
            }
        }
        return p;
    }

    const w = width * size;
    const h = height * size;
    let pathData = '';
    for (let i = 0; i < pathList.length; i++) {
        pathData += path(pathList[i].curve);
    }

    let svg =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' +
        w +
        ' ' +
        h +
        '" width="' +
        w +
        '" height="' +
        h +
        '">';
    if (pathData) {
        svg += '<path d="' + pathData + '" fill="' + color + '" fill-rule="evenodd" stroke="none" />';
    }
    svg += '</svg>';
    return svg;
}

function loadImageSource(source) {
    return new Promise((resolve, reject) => {
        if (source instanceof HTMLImageElement) {
            if (source.complete) {
                resolve(source);
            } else {
                source.onload = () => resolve(source);
                source.onerror = reject;
            }
            return;
        }

        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;

        if (source instanceof File || source instanceof Blob) {
            const reader = new FileReader();
            reader.onload = (e) => {
                image.src = e.target.result;
            };
            reader.onerror = reject;
            reader.readAsDataURL(source);
        } else if (typeof source === 'string') {
            image.src = source;
        } else {
            reject(new Error('Unsupported source format for Potrace'));
        }
    });
}

/**
 * Vectorize an image file/source to SVG using potrace-js
 * @param {File|Blob|string|HTMLImageElement} source
 * @param {Object} customOptions
 * @returns {Promise<{svg: string, blob: Blob, file: File, width: number, height: number, paths: Array}>}
 */
export async function vectorize(source, customOptions = {}) {
    const options = { ...DEFAULT_OPTIONS, ...customOptions };

    let originalFileName = 'image.png';
    if (source instanceof File) {
        originalFileName = source.name;
    } else if (typeof source === 'string' && !source.startsWith('data:')) {
        originalFileName = source.split('/').pop().split('?')[0] || 'image.png';
    }
    const svgFileName = originalFileName.replace(/\.[^/.]+$/, '') + '.svg';

    const image = await loadImageSource(source);
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0);

    const bitmap = createBitmapFromCanvas(canvas, options.threshold);
    const pathList = traceBitmap(bitmap, options);

    const scale = options.scale || 1;
    const svgString = pathListToSVG(pathList, canvas.width, canvas.height, scale, options.color || 'black');
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const file = new File([blob], svgFileName, { type: 'image/svg+xml' });

    return {
        svg: svgString,
        blob: blob,
        file: file,
        width: canvas.width * scale,
        height: canvas.height * scale,
        paths: pathList,
    };
}

export const Potrace = {
    ...PotraceJS,
    Bitmap,
    traceBitmap,
    traceCanvas,
    traceImage,
    traceUrl,
    getPaths,
    vectorize,
};

if (typeof window !== 'undefined') {
    window.Potrace = Potrace;
}

export default Potrace;
